import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Select,
  SearchableSelect,
  Card,
  Badge,
  getStatusBadgeVariant,
  Pagination,
  Modal,
  ModalFooter,
  CompactFilterBar,
  Table,
} from '@/components/common';
import type { Column, SortDirection } from '@/components/common/Table';
import {
  useLeads,
  useConvertLead,
  useBulkConvertLeads,
  useBulkAssignLeads,
  useAssignableUsers,
} from '@/hooks/useLeads';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { formatCurrency, titleCase } from '@/utils';
import type { Lead, LeadFilters, LeadStatus, LeadPriority } from '@/types';
import { useAuthStore } from '@/store';
import { useCan } from '@/hooks/useEmployeeRights';
import type { LeadBulkConvertResult } from '@/services/leads';

// #273: the bulk-convert endpoint reports every id it did NOT convert, with a
// machine reason. Showing "3 skipped" and nothing else would be the same
// silence the endpoint was built to end, so each reason gets a sentence.
// #491: why the row checkbox is disabled for won leads.
const WON_LEAD_SELECT_HINT = 'Won leads cannot be selected';

const SKIP_REASONS: Record<string, string> = {
  not_found: 'not found for your broker',
  already_converted: 'already converted to a customer',
  no_contact_details: 'has neither a phone number nor an email address',
  customer_not_in_broker: 'the chosen customer belongs to another broker',
  error: 'could not be converted (unexpected error)',
};

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'nurturing', label: 'Nurturing' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'won', label: 'Converted (Won)' },
  { value: 'lost', label: 'Lost' },
  { value: 'junk', label: 'Junk' },
];

// Lead.PRIORITY_CHOICES is low|medium|high|hot -- "Urgent" filtered on a
// value no lead can hold and therefore always returned nothing.
const priorityOptions = [
  { value: '', label: 'All Priority' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'hot', label: 'Hot' },
];

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Module-level, because `useFilterParams` reads it once to learn both the keys
 * it owns and each key's SHAPE (`''` is a plain string).
 *
 * DELIBERATELY NOT HERE: a created-date window. `leadService.getLeads` puts
 * `date_from`/`date_to` on the wire as DRF's `created_at_after` /
 * `created_at_before`, but `LeadViewSet` declares `filterset_fields` with no
 * `created_at` entry and no `filterset_class`
 * (backend/apps/leads/views.py:332-343), so `DjangoFilterBackend` builds its
 * FilterSet from that list alone and DISCARDS both params -- the full,
 * unfiltered book comes back. A control that reports a narrowing it did not
 * perform is worse than no control, so the boxes are gone until a `LeadFilter`
 * FilterSet lands server-side (the `InAppNotificationFilter` in
 * apps/communications/views.py exists for exactly this bug on that endpoint).
 *
 * DELIBERATELY NOT HERE: the page number. Every filter change on this page
 * resets it to 1 and the debounced-search effect re-asserts that on mount, so
 * a page number restored from the URL would be clobbered before first paint --
 * a URL reading `page=3` over a list showing page 1 is worse than not
 * restoring it at all.
 */
type LeadPanelFilters = {
  search: string;
  status: string;
  priority: string;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: LeadPanelFilters = {
  search: '',
  status: '',
  priority: '',
  sort: '',
  sort_dir: '',
};

/**
 * The house namespace for a page's own filters.
 *
 * This page honours NO unprefixed deep-link params today -- nothing in the app
 * links to `/leads?…` with a filter on it (the public site CREATES leads, it
 * does not link into this list), and `converted_to_customer` is read by
 * `leadService` on a contact screen, never as a URL on this route. So there is
 * no seed to consume and no `DEEP_LINK_PARAMS` list: every param this page
 * reads or writes is its own, under `f_`.
 *
 * The prefix is kept anyway. It costs nothing, it matches Policies and every
 * other adopter, and the day a `/leads?source=<id>` link is added the filters
 * are already in a namespace that cannot collide with it -- which is the bug
 * (a one-shot deep link turned permanent by the first dropdown touch) the
 * prefix exists to prevent.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the ONLY fields `LeadViewSet.ordering_fields` accepts
 * (backend/apps/leads/views.py:345). DRF's OrderingFilter silently DROPS a
 * term it does not recognise and returns the default order, so a header marked
 * sortable on anything outside this set renders as a control, sends its click,
 * and changes nothing -- a bug this project has shipped before.
 *
 * Of the columns this page shows, only `estimated_premium` is in it.
 * `full_name`, `source__name`, `priority` and `status` are NOT orderable
 * server-side; making those headers sortable needs a backend change first, not
 * a frontend flag.
 */
const SORTABLE_FIELDS = new Set(['created_at', 'lead_score', 'next_follow_up', 'estimated_premium']);

export function LeadsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link. Restored SYNCHRONOUSLY in the
  // hook's lazy initializer, so the very first render -- and the very first
  // request -- is already filtered, and CompactFilterBar opens its panel over
  // a filtered list instead of hiding the reason for it.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<LeadPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms)
  // feeds `filters.search`. Seeded from the restored `?f_search=` so a refresh
  // brings the box back filled in, not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // Item 1: this page is now reachable by a POSP, whose list is exactly the
  // leads assigned to them. Bulk convert and bulk assign are broker-staff
  // actions and stay 403 server-side (LeadViewSet.POSP_ACTIONS), so the
  // selection column that exists only to feed them is not rendered for a
  // POSP -- offering a control whose only possible answer is 403 is worse
  // than not offering it.
  const currentRole = useAuthStore((s) => s.user?.role);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isPosp = currentRole === 'posp' || currentRole === 'super_posp';
  // P1 (15 Sep): a POSP converts ONE lead at a time -- their own. Bulk convert
  // stays broker-staff only.
  //
  // B4: converting is leads Edit AND contacts Create for everyone (it writes
  // the lead and creates the customer), which is what `convert` and
  // `bulk_convert` check on the API.
  const canEditLeads = useCan('leads', 'edit');
  const canCreateContacts = useCan('contacts', 'create');
  // B4: /leads/new requires leads Create; never offer a link that route refuses.
  const canCreateLeads = useCan('leads', 'create');
  const canAddCustomer = canEditLeads && canCreateContacts;
  const convertOne = useConvertLead();
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const pospCanConvert = (row: Lead) =>
    isPosp && canAddCustomer && row.status !== 'won' && row.assigned_to === currentUserId;

  // U-04: only a field the server can order by, so a stale or hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  // The wire shape. Memoized because it is the react-query key: rebuilding it
  // every render would refetch the list on every unrelated re-render.
  const queryFilters = useMemo<LeadFilters>(
    () => ({
      search: filters.search || undefined,
      status: (filters.status || undefined) as LeadStatus | undefined,
      priority: (filters.priority || undefined) as LeadPriority | undefined,
      ordering,
    }),
    [filters, ordering]
  );

  const { data, isLoading, isError, refetch } = useLeads(page, queryFilters);
  const leads = useMemo(() => data?.results ?? [], [data]);
  const showPospActions = isPosp && canAddCustomer;
  const bulkConvert = useBulkConvertLeads();
  const bulkAssign = useBulkAssignLeads();
  const { data: assignableUsers = [], isLoading: assignableLoading } = useAssignableUsers(!isPosp);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastResult, setLastResult] = useState<LeadBulkConvertResult | null>(null);

  // A won lead is already a customer; the endpoint would answer
  // `already_converted` for it, so it is not offerable.
  const convertibleLeads = useMemo(() => leads.filter((l) => l.status !== 'won'), [leads]);
  const allOnPageSelected =
    convertibleLeads.length > 0 && convertibleLeads.every((l) => selectedIds.has(l.id));

  // A selection is only meaningful for the rows on screen: keeping it across a
  // page or filter change would convert leads the user can no longer see.
  useEffect(() => {
    setSelectedIds(new Set());
    setLastResult(null);
  }, [page, queryFilters]);

  const toggleLead = (leadId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds(allOnPageSelected ? new Set() : new Set(convertibleLeads.map((l) => l.id)));
  };

  const handleBulkConvert = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkConvert.mutate(ids, {
      onSuccess: (result) => {
        setLastResult(result);
        setSelectedIds(new Set());
      },
    });
  };

  const leadLabel = (leadId: string) =>
    leads.find((l) => l.id === leadId)?.full_name || leadId;
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Push the debounced term into the filters and reset to the first page
  // whenever the effective search changes.
  //
  // Keyed off a ref of the LAST TERM PUSHED, not off `filters.search`. Seeded
  // with the restored value, it makes the mount pass a no-op (the box and the
  // filter already agree, so no second request goes out); and because Clear
  // resets `filters.search` while `debouncedTerm` still holds the old text for
  // another 300ms, comparing against `filters.search` here would immediately
  // re-apply the search the user just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setPage(1);
  }, [debouncedTerm, setFilter]);

  const handleFilterChange = (key: keyof LeadPanelFilters, value: string) => {
    setFilters({ [key]: value } as Partial<LeadPanelFilters>);
    setPage(1);
  };

  // U-04: server-side sort. `Table` never reorders the rows it is given -- it
  // reports the next sort state and this sends it as DRF's `?ordering=`, so
  // the order is the whole filtered book's, not this page of twenty.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  // The PANEL's own controls only. The search box is always visible so it
  // never badges, and the sort is a view preference rather than a filter.
  const activeFilterCount = [filters.status, filters.priority].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    // Q-01: the one action that empties the `?f_*` namespace. It also resets
    // the sort, which lives in that namespace so a shared link reproduces the
    // view the sender was looking at. There is no second (unprefixed) namespace
    // on this page to consume -- see FILTER_PREFIX.
    clearAll();
    setSelectedIds(new Set());
    setLastResult(null);
    setPage(1);
  };

  const handleRowClick = (lead: Lead) => {
    navigate(`/leads/${lead.id}`);
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'hot':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  /**
   * U-02: the columns, each sized as CONTENT + CELL PADDING.
   *
   * `Table` pads every `<td>` with `px-6` (48px) and, under `layout="fixed"`,
   * sets `truncate` -- i.e. `overflow: hidden`, with no scrollbar and no
   * ellipsis on an atomic inline box. A column declared at 7rem therefore
   * offers 64px of content, not 112px, and anything wider is CLIPPED. The
   * tight columns override the padding to `px-3` (24px) through `className`
   * (`cn` is tailwind-merge, so a `px-*` there beats the `px-6`):
   *
   *   select       3rem   px-3 ->  24px (16px checkbox + its focus ring)
   *   lead        16rem   px-6 -> 208px (40px avatar + 12px gap + 156px name)
   *   contact     13rem   px-6 -> 160px (a phone number and an email)
   *   source      11rem   px-6 -> 128px
   *   premium      9.5rem px-3 -> 128px (header "EST. PREMIUM" + sort icon)
   *   priority     7.5rem px-3 ->  96px (badge "Medium" ~60px)
   *   status       9.5rem px-3 -> 128px (badge "Proposal Sent" ~100px)
   *   actions     14rem   px-3 -> 200px (the POSP Convert button, ~181px:
   *                              px-3 24 + a 16px icon + mr-2 8 + ~133px of text)
   *
   * Broker staff see select + the seven text columns = 69.5rem (1112px); a
   * POSP sees no select column but does get actions = 80.5rem (1288px). Both sit
   * inside the ~1424px that a 1440-1536px desktop can show without a
   * horizontal PAGE scrollbar; narrower than that the table -- not the page --
   * scrolls sideways inside its own card.
   *
   * THE CELLS THAT RENDER JSX CARRY THEIR OWN `title`. `Table` sets a tooltip
   * only when a cell renders to a string or a number (stringifying arbitrary
   * JSX would show "[object Object]"), so money, names and badges would
   * otherwise be cut off with nothing to hover. Both badges additionally get
   * `max-w-full` with the label in a `truncate` span: an `inline-flex` badge is
   * an atomic inline-level box, which a `truncate` ancestor hard-CLIPS with no
   * ellipsis at all.
   */
  const columns: Column<Lead>[] = [
    ...(!isPosp
      ? [
          {
            key: 'select',
            header: '',
            width: '3rem',
            className: 'px-3',
            render: (lead: Lead) => (
              // The row navigates on click, so the checkbox has to stop the
              // bubble or ticking a box would open the lead.
              <span
                className="inline-flex"
                onClick={(event) => event.stopPropagation()}
                title={lead.status === 'won' ? WON_LEAD_SELECT_HINT : undefined}
              >
                <input
                  type="checkbox"
                  aria-label={`Select ${lead.full_name}`}
                  title={lead.status === 'won' ? WON_LEAD_SELECT_HINT : undefined}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 dark:text-primary-400 focus:ring-primary-500 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700"
                  checked={selectedIds.has(lead.id)}
                  disabled={lead.status === 'won'}
                  onChange={() => toggleLead(lead.id)}
                />
              </span>
            ),
          } as Column<Lead>,
        ]
      : []),
    {
      key: 'lead',
      header: 'Lead',
      width: '16rem',
      render: (lead: Lead) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 shrink-0 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
              {lead.first_name?.[0] ?? ''}
              {lead.last_name?.[0] ?? ''}
            </span>
          </div>
          <div className="min-w-0">
            <p
              className="truncate text-sm font-medium text-gray-900 dark:text-white"
              title={lead.full_name}
            >
              {lead.full_name}
            </p>
            <p
              className="truncate text-xs text-gray-500 dark:text-gray-400"
              title={lead.company_name || lead.lead_number}
            >
              {lead.company_name || lead.lead_number}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      width: '13rem',
      render: (lead: Lead) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-gray-900 dark:text-white" title={lead.phone}>
            {lead.phone}
          </p>
          {lead.email && (
            <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={lead.email}>
              {lead.email}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      width: '11rem',
      render: (lead: Lead) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-gray-900 dark:text-white" title={lead.source_name}>
            {lead.source_name}
          </p>
          {lead.campaign_name && (
            <p
              className="truncate text-xs text-gray-500 dark:text-gray-400"
              title={lead.campaign_name}
            >
              {lead.campaign_name}
            </p>
          )}
        </div>
      ),
    },
    {
      // U-04: `estimated_premium` IS in LeadViewSet.ordering_fields -- the one
      // column on this page that is.
      key: 'estimated_premium',
      header: 'Est. Premium',
      width: '9.5rem',
      className: 'px-3',
      sortable: true,
      render: (lead: Lead) => {
        const label = lead.estimated_premium ? formatCurrency(lead.estimated_premium) : '-';
        return (
          <p className="truncate text-sm text-gray-900 dark:text-white" title={label}>
            {label}
          </p>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '7.5rem',
      className: 'px-3',
      render: (lead: Lead) => (
        <Badge variant={getPriorityBadgeVariant(lead.priority)} size="sm" className="max-w-full">
          <span className="truncate" title={titleCase(lead.priority)}>
            {titleCase(lead.priority)}
          </span>
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '9.5rem',
      className: 'px-3',
      render: (lead: Lead) => (
        <Badge variant={getStatusBadgeVariant(lead.status)} size="sm" className="max-w-full">
          <span className="truncate" title={titleCase(lead.status)}>
            {titleCase(lead.status)}
          </span>
        </Badge>
      ),
    },
    ...(showPospActions
      ? [
          {
            key: 'actions',
            header: 'Actions',
            width: '14rem',
            className: 'px-3',
            render: (lead: Lead) =>
              pospCanConvert(lead) ? (
                <span className="inline-flex max-w-full" onClick={(event) => event.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<UserPlusIcon className="w-4 h-4" />}
                    aria-label={`Convert ${lead.full_name} to customer`}
                    isLoading={convertOne.isPending && convertingId === lead.id}
                    disabled={convertOne.isPending}
                    onClick={() => {
                      setConvertingId(lead.id);
                      convertOne.mutate(lead.id, {
                        onSettled: () => setConvertingId(null),
                      });
                    }}
                  >
                    Convert to Customer
                  </Button>
                </span>
              ) : null,
          } as Column<Lead>,
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle={`${totalItems} total leads`}
        breadcrumbs={[{ label: 'Leads' }]}
        actions={
          canCreateLeads ? (
            <Link to="/leads/new">
              <Button leftIcon={<PlusIcon className="w-5 h-5" />}>Add Lead</Button>
            </Link>
          ) : undefined
        }
      />

      {/* One line by default: the search box plus a Filter toggle badged with
          the active count. The panel starts expanded whenever a filter is
          already set -- including one restored from `?f_*` after a refresh --
          so a filtered list is never shown with nothing on screen to explain
          why. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by name, email, phone, or company..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        <SearchableSelect
          id="lead-status-filter"
          label="Status"
          options={statusOptions}
          value={filters.status}
          onChange={(v) => handleFilterChange('status', v)}
          placeholder="All Status"
        />
        <Select
          id="lead-priority-filter"
          label="Priority"
          options={priorityOptions}
          value={filters.priority}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
        />
        {/* NO created-date control. The old bar carried a single bare, unlabelled
            date box; this sweep briefly grew it into a labelled From/To range
            before measuring the other end of the wire. `LeadViewSet` cannot
            filter on `created_at` at all (see EMPTY_FILTERS), so every version
            of this control returned the whole book while the Filter badge
            claimed a narrowing. Restore it in the same commit as the backend
            `LeadFilter`, not before. */}
      </CompactFilterBar>

      {!isPosp && selectedIds.size > 0 && (
        <Card className="mb-6 border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {selectedIds.size} selected
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
              {/* Bulk assign shares the selection with Convert but NOT its
                  eligibility rule: an already-converted lead still needs an
                  owner, so `selectedIds` is used directly here rather than
                  `convertibleLeads`. */}
              {canEditLeads && (
                <Button variant="outline" onClick={() => setShowBulkAssign(true)}>
                  Assign to…
                </Button>
              )}
              {canAddCustomer && (
                <Button
                  leftIcon={<UserPlusIcon className="w-5 h-5" />}
                  isLoading={bulkConvert.isPending}
                  onClick={handleBulkConvert}
                >
                  Convert to Customer
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {lastResult && (
        <Card className="mb-6">
          <div role="status" className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p className="font-medium text-gray-900 dark:text-white">
              {lastResult.converted.length} lead
              {lastResult.converted.length === 1 ? '' : 's'} converted to customers
            </p>
            {lastResult.skipped.length > 0 && (
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {lastResult.skipped.length} skipped
                </p>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {lastResult.skipped.map((skip) => (
                    <li key={skip.id}>
                      {leadLabel(skip.id)} &mdash; {SKIP_REASONS[skip.reason] ?? skip.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* A REFRESH that failed while the rows it was replacing are still in
          hand -- react-query keeps the previous page's data on a refetch of the
          same key. Without this the list silently goes stale behind a
          successful-looking screen. */}
      {isError && leads.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Could not refresh this list. These are the results from before your last change.</span>
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {/* Three states, never two: loading, a failed request and an empty book
          are different facts, and this codebase's signature defect is the
          middle one rendering as the last. */}
      {isLoading ? (
        // The shared Table's own skeleton, which replaced a hand-rolled one
        // that had no dark-mode classes at all.
        <Table<Lead> data={[]} columns={columns} isLoading rowKey={(lead) => lead.id} />
      ) : isError && leads.length === 0 ? (
        <Card padding="none">
          <div role="alert" className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              Could not load leads. This does not mean you have none.
            </p>
            <button
              type="button"
              className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        </Card>
      ) : leads.length === 0 ? (
        <Card padding="none">
          <EmptyState canCreate={canCreateLeads} />
        </Card>
      ) : (
        <>
          {/* U-02: the select-all control. It used to live in the header row;
              the shared Table's headers are text, so it moved here rather than
              keeping a hand-rolled <table> for one checkbox. Same label, same
              behaviour. */}
          {!isPosp && (
            <div className="mb-3 flex items-center gap-2">
              <input
                id="lead-select-all"
                type="checkbox"
                aria-label="Select all on this page"
                className="w-4 h-4 rounded border-gray-300 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                checked={allOnPageSelected}
                disabled={convertibleLeads.length === 0}
                onChange={toggleAllOnPage}
              />
              <label htmlFor="lead-select-all" className="text-sm text-gray-600 dark:text-gray-400">
                Select all on this page
              </label>
            </div>
          )}
          <Table<Lead>
            data={leads}
            columns={columns}
            rowKey={(lead) => lead.id}
            onRowClick={handleRowClick}
            // U-02: declared widths (no column jump when a filter changes which
            // rows are on screen) and a header that stays put while they scroll.
            layout="fixed"
            stickyHeader
            // U-04: the Table never reorders anything -- it reports the next
            // sort state and the server does the ordering.
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}

      <Modal
        isOpen={showBulkAssign}
        onClose={() => setShowBulkAssign(false)}
        title={`Assign ${selectedIds.size} lead${selectedIds.size === 1 ? '' : 's'}`}
      >
        <div className="space-y-4">
          {assignableLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading your team…</p>
          ) : assignableUsers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You have no employees or agents to assign these leads to yet.
            </p>
          ) : (
            <Select
              label="Assign to"
              name="bulk_assign_to"
              options={[
                { value: '', label: 'Select a person' },
                ...assignableUsers.map((candidate) => ({
                  value: candidate.id,
                  label: candidate.full_name,
                })),
              ]}
              value={bulkAssignTo}
              onChange={(e) => setBulkAssignTo(e.target.value)}
            />
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowBulkAssign(false)}>
            Cancel
          </Button>
          <Button
            disabled={!bulkAssignTo}
            isLoading={bulkAssign.isPending}
            onClick={() =>
              bulkAssign.mutate(
                { leadIds: Array.from(selectedIds), assignedTo: bulkAssignTo },
                {
                  onSuccess: () => {
                    setShowBulkAssign(false);
                    setBulkAssignTo('');
                    setSelectedIds(new Set());
                  },
                },
              )
            }
          >
            Assign
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
}

function EmptyState({ canCreate }: { canCreate: boolean }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No leads found</h3>
      {canCreate && (
        <>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by adding your first lead.
          </p>
          <div className="mt-6">
            <Link to="/leads/new">
              <Button leftIcon={<PlusIcon className="w-5 h-5" />}>Add Lead</Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
